import * as React from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Home from "./pages/Home";
import About from "./pages/About";
import Services from "./pages/Services";
import Gallery from "./pages/Gallery";
import Projects from "./pages/Projects";
import Offers from "./pages/Offers";
import Areas from "./pages/Areas";
import Contact from "./pages/Contact";
import AdsLinks from "./pages/AdsLinks";
import NotFound from "./pages/NotFound";
import MorrisCounty from "./pages/areas/MorrisCounty";
import SussexCounty from "./pages/areas/SussexCounty";
import WarrenCounty from "./pages/areas/WarrenCounty";
import HunterdonCounty from "./pages/areas/HunterdonCounty";
import SomersetCounty from "./pages/areas/SomersetCounty";
import TownPageWrapper from "./pages/areas/TownPageWrapper";

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold">Something went wrong</h1>
            <button
              className="mt-4 rounded bg-blue-500 px-4 py-2 text-white"
              onClick={() => this.setState({ hasError: false })}
            >
              Try again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => (
  <React.Suspense fallback={<div>Loading...</div>}>
    <ErrorBoundary>
      <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/services" element={<Services />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/offers" element={<Offers />} />
              <Route path="/areas" element={<Areas />} />
              <Route path="/areas/morris-county" element={<MorrisCounty />} />
              <Route path="/areas/sussex-county" element={<SussexCounty />} />
              <Route path="/areas/warren-county" element={<WarrenCounty />} />
              <Route path="/areas/hunterdon-county" element={<HunterdonCounty />} />
              <Route path="/areas/somerset-county" element={<SomersetCounty />} />
              <Route path="/areas/:townSlug" element={<TownPageWrapper />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/ads-links" element={<AdsLinks />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  </React.Suspense>
);

export default App;
